import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { Input } from "@/components/ui/input";
import { Flame, LogOut, Download, Trash2, Mail, ShieldCheck, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sq_admin_token";

const TYPE_LABELS = {
  school: "School / Daycare",
  sponsor: "Sponsorship",
  robotics: "Pluggy Robot",
  content: "Songs & Animations",
  general: "General",
};

function toCSV(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => esc(r[h.key])).join(",")).join("\n");
  return `${head}\n${body}`;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
      toast.success("Welcome back, Safety Hero!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] grid place-items-center bg-sq-ink px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md bg-white rounded-[2rem] border-4 border-sq-ink p-8 shadow-[0_10px_0_#DC2626]">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-sq-red text-white"><Flame size={20} /></span>
          <span className="font-display text-2xl tracking-wide pt-1">SAFETY QUEST ADMIN</span>
        </div>
        <label className="text-sm font-semibold mb-2 block">Email</label>
        <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@thesafetyquest.com" className="h-12 rounded-xl border-2 border-sq-ink/15 mb-4" />
        <label className="text-sm font-semibold mb-2 block">Password</label>
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-xl border-2 border-sq-ink/15" />
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-6 w-full py-4 rounded-full bg-sq-red text-white font-bold text-lg btn-chunky disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  useSEO({ title: "Admin", description: "The Safety Quest admin dashboard.", noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("inquiries");
  const [contacts, setContacts] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [c, p] = await Promise.all([
        axios.get(`${API}/contact`, cfg),
        axios.get(`${API}/pledges`, cfg),
      ]);
      setContacts(c.data);
      setPledges(p.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  const removeContact = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportContacts = () => download("inquiries.csv", toCSV(contacts, [
    { key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
    { key: "organization", label: "Organization" }, { key: "inquiry_type", label: "Type" }, { key: "message", label: "Message" },
  ]));

  const exportPledges = () => download("pledges.csv", toCSV(pledges, [
    { key: "created_at", label: "Date" }, { key: "hero_name", label: "Hero Name" }, { key: "age", label: "Age" },
  ]));

  if (!token) return <LoginForm onLogin={setToken} />;

  const fmt = (d) => new Date(d).toLocaleString();

  return (
    <div className="min-h-[100svh] bg-sq-bg">
      <header className="bg-sq-ink text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-sq-red text-white"><Flame size={18} /></span>
            <span className="font-display text-2xl tracking-wide pt-1">SAFETY QUEST ADMIN</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 font-semibold text-sm transition-colors">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl bg-white border-2 border-sq-ink p-6">
            <Mail className="text-sq-red mb-2" />
            <p className="font-display text-5xl">{contacts.length}</p>
            <p className="text-sq-ink/60 font-semibold text-sm">Inquiries</p>
          </div>
          <div className="rounded-2xl bg-white border-2 border-sq-ink p-6">
            <ShieldCheck className="text-sq-blue mb-2" />
            <p className="font-display text-5xl">{pledges.length}</p>
            <p className="text-sq-ink/60 font-semibold text-sm">Hero Pledges</p>
          </div>
          <div className="rounded-2xl bg-white border-2 border-sq-ink p-6">
            <Users className="text-sq-ink mb-2" />
            <p className="font-display text-5xl">{contacts.filter((c) => c.inquiry_type === "robotics").length}</p>
            <p className="text-sq-ink/60 font-semibold text-sm">Pluggy Robot Leads</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="inline-flex rounded-full bg-white border-2 border-sq-ink p-1">
            <button onClick={() => setTab("inquiries")} data-testid="tab-inquiries" className={`px-5 py-2 rounded-full font-semibold text-sm ${tab === "inquiries" ? "bg-sq-ink text-white" : ""}`}>Inquiries</button>
            <button onClick={() => setTab("pledges")} data-testid="tab-pledges" className={`px-5 py-2 rounded-full font-semibold text-sm ${tab === "pledges" ? "bg-sq-ink text-white" : ""}`}>Pledges</button>
          </div>
          <button onClick={tab === "inquiries" ? exportContacts : exportPledges} data-testid="export-csv" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sq-yellow text-sq-ink font-bold text-sm btn-chunky">
            <Download size={16} /> Export CSV
          </button>
        </div>

        {loading ? (
          <p className="text-sq-ink/50 py-16 text-center">Loading…</p>
        ) : tab === "inquiries" ? (
          <div className="overflow-x-auto rounded-2xl border-2 border-sq-ink bg-white">
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead className="bg-sq-ink text-white text-left">
                <tr>
                  <th className="p-4">Date</th><th className="p-4">Name</th><th className="p-4">Email</th>
                  <th className="p-4">Org</th><th className="p-4">Type</th><th className="p-4">Message</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sq-ink/50">No inquiries yet.</td></tr>}
                {contacts.map((c) => (
                  <tr key={c.id} data-testid={`inquiry-row-${c.id}`} className="border-t border-sq-ink/10 align-top">
                    <td className="p-4 whitespace-nowrap text-sq-ink/60">{fmt(c.created_at)}</td>
                    <td className="p-4 font-semibold">{c.name}</td>
                    <td className="p-4"><a href={`mailto:${c.email}`} className="text-sq-blue hover:underline">{c.email}</a></td>
                    <td className="p-4">{c.organization || "—"}</td>
                    <td className="p-4"><span className="inline-block px-2.5 py-1 rounded-full bg-sq-red/10 text-sq-red text-xs font-bold whitespace-nowrap">{TYPE_LABELS[c.inquiry_type] || c.inquiry_type}</span></td>
                    <td className="p-4 max-w-xs">{c.message}</td>
                    <td className="p-4"><button onClick={() => removeContact(c.id)} data-testid={`delete-${c.id}`} className="text-sq-ink/40 hover:text-sq-red"><Trash2 size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border-2 border-sq-ink bg-white">
            <table className="w-full text-sm" data-testid="pledges-table">
              <thead className="bg-sq-ink text-white text-left">
                <tr><th className="p-4">Date</th><th className="p-4">Hero Name</th><th className="p-4">Age</th></tr>
              </thead>
              <tbody>
                {pledges.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-sq-ink/50">No pledges yet.</td></tr>}
                {pledges.map((p) => (
                  <tr key={p.id} className="border-t border-sq-ink/10">
                    <td className="p-4 whitespace-nowrap text-sq-ink/60">{fmt(p.created_at)}</td>
                    <td className="p-4 font-semibold">{p.hero_name}</td>
                    <td className="p-4">{p.age || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
